import { useEffect, useState } from 'react'

const WATERMARK_TEXT = "Lucie's Book"
const WATERMARK_OPACITY = 0.2
const WATERMARK_ANGLE = -Math.PI / 6

async function loadImage(blob: Blob): Promise<HTMLImageElement> {
  const objectUrl = URL.createObjectURL(blob)
  try {
    const image = new Image()
    image.decoding = 'async'
    image.src = objectUrl
    await image.decode()
    return image
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('Unable to create preview image')),
      'image/jpeg',
      0.92,
    )
  })
}

async function createWatermarkedPreview(src: string, signal: AbortSignal): Promise<string> {
  const response = await fetch(src, { signal })
  if (!response.ok) throw new Error(`Unable to load preview image (${response.status})`)

  const image = await loadImage(await response.blob())
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError')

  const canvas = document.createElement('canvas')
  canvas.width = image.naturalWidth
  canvas.height = image.naturalHeight
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Canvas is unavailable')

  // JPEG has no alpha channel, so give transparent uploads a predictable background.
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, canvas.width, canvas.height)
  context.drawImage(image, 0, 0)

  const shortEdge = Math.min(canvas.width, canvas.height)
  const fontSize = Math.max(24, Math.round(shortEdge * 0.065))
  context.font = `600 ${fontSize}px Georgia, serif`
  context.textBaseline = 'middle'
  const textWidth = context.measureText(WATERMARK_TEXT).width
  // Leave generous whitespace while keeping several marks visible after object-fit cropping.
  const columnStep = textWidth + fontSize * 4.25
  const rowStep = fontSize * 5.25
  const drawingRadius = Math.hypot(canvas.width, canvas.height)

  context.save()
  context.translate(canvas.width / 2, canvas.height / 2)
  context.rotate(WATERMARK_ANGLE)
  context.globalAlpha = WATERMARK_OPACITY
  context.fillStyle = '#ffffff'
  context.strokeStyle = '#222222'
  context.lineWidth = Math.max(1, fontSize * 0.035)

  let row = 0
  for (let y = -drawingRadius; y <= drawingRadius; y += rowStep) {
    const offset = row % 2 === 0 ? 0 : columnStep / 2
    for (let x = -drawingRadius - offset; x <= drawingRadius; x += columnStep) {
      context.strokeText(WATERMARK_TEXT, x, y)
      context.fillText(WATERMARK_TEXT, x, y)
    }
    row += 1
  }
  context.restore()

  const previewBlob = await canvasToBlob(canvas)
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError')
  return URL.createObjectURL(previewBlob)
}

export function WatermarkedImage({ src, alt, className, coverLayout = false }: {
  src: string
  alt: string
  className?: string
  coverLayout?: boolean
}) {
  const [previewUrl, setPreviewUrl] = useState<string>()
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    let activePreviewUrl: string | undefined
    setPreviewUrl(undefined)
    setFailed(false)

    createWatermarkedPreview(src, controller.signal)
      .then(url => {
        if (controller.signal.aborted) {
          URL.revokeObjectURL(url)
          return
        }
        activePreviewUrl = url
        setPreviewUrl(url)
      })
      .catch(error => {
        if (error instanceof DOMException && error.name === 'AbortError') return
        console.error('Failed to create watermarked preview', error)
        setFailed(true)
      })

    return () => {
      controller.abort()
      if (activePreviewUrl) URL.revokeObjectURL(activePreviewUrl)
    }
  }, [src])

  if (failed) return <span className="watermarked-image-status">Preview unavailable</span>
  if (!previewUrl) return <span className="watermarked-image-status">Preparing preview…</span>
  if (coverLayout) return <img className="book-cover-art-foreground" src={previewUrl} alt={alt} draggable={false} />
  return <img className={className} src={previewUrl} alt={alt} draggable={false} />
}
